import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { duplicateItemToUserIngredients, getBaseIngredients } from '@/service/base_ingredient_service'
import { getHiddenBaseIngredients } from '@/service/hidden_base_ingredient_service'
import { getUserRecipes, deleteRecipeFromUserCollection } from '@/service/recipe_service'

export async function addIngredientToUserCollection(userId, ingredientId, ingredientData) {
  const docRef = doc(db, "Ingredients", `${userId}-ingredients`);

  try {
    const docSnap = await getDoc(docRef);
    const existingData = docSnap.exists() ? docSnap.data() : {};

    if (Object.prototype.hasOwnProperty.call(existingData, ingredientId)) {
      return { alreadyExists: true };
    }

    const updatedData = {
      ...existingData,
      [ingredientId]: ingredientData,
    };

    await setDoc(docRef, updatedData);
    return { alreadyExists: false };
  } catch (error) {
    console.error("❌ Erreur lors de l'ajout de l'ingrédient :", error);
    throw error;
  }
}

/**
 * Duplique un élément (ingrédient de base ou autre item) dans la collection Ingredients/<userId>-ingredients
 * en s'appuyant sur le helper centralisé du food_manager.
 * Retourne { id, existed }
 */
export async function addItemToUserCollection(userId, item, itemId = null) {
  return duplicateItemToUserIngredients(userId, item, itemId)
}

/**
 * Ajoute un lot d'ingrédients sans écraser le document complet.
 * - Calcule ce qui existe déjà vs ce qui est nouveau
 * - N'écrit que les nouveaux (merge: true)
 * - Retourne { added, already }
 */
export async function addIngredientsBulkToUserCollection(userId, entriesMap) {
  const docRef = doc(db, "Ingredients", `${userId}-ingredients`);

  // 1) Lire l'existant
  const snap = await getDoc(docRef);
  const existing = snap.exists() ? snap.data() : {};
  const existingIds = new Set(Object.keys(existing));

  // 2) Séparer nouveaux / déjà présents
  const incomingIds = Object.keys(entriesMap || {});
  const toInsertIds = incomingIds.filter((id) => !existingIds.has(id));
  const alreadyIds  = incomingIds.filter((id) =>  existingIds.has(id));

  // 3) Si nouveaux, écrire uniquement ceux-là
  if (toInsertIds.length > 0) {
    const payload = {};
    for (const id of toInsertIds) {
      payload[id] = entriesMap[id];
    }
    await setDoc(docRef, payload, { merge: true });
  }

  return {
    added: toInsertIds.length,
    already: alreadyIds.length,
  };
}

/**
 * Supprime un ingrédient de la collection d'un utilisateur.
 * Supprime également toutes les recettes qui contiennent cet ingrédient.
 * @param {string} userId - L'ID de l'utilisateur.
 * @param {string} ingredientId - L'ID de l'ingrédient à supprimer.
 * @returns {Promise<{deletedRecipes: number}>} - Nombre de recettes supprimées
 */
export async function deleteIngredientFromUserCollection(userId, ingredientId) {
  const docRef = doc(db, "Ingredients", `${userId}-ingredients`);
  let deletedRecipesCount = 0;

  try {
    // 1. Vérifier que l'ingrédient existe
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      console.warn("⚠️ Aucun document d'ingrédients trouvé.");
      return { deletedRecipes: 0 };
    }

    const data = docSnap.data();
    if (!Object.prototype.hasOwnProperty.call(data, ingredientId)) {
      console.warn("⚠️ L'ingrédient n'existe pas dans la collection.");
      return { deletedRecipes: 0 };
    }

    // 2. Récupérer toutes les recettes de l'utilisateur
    const recipes = await getUserRecipes(userId);
    const recipesToDelete = [];

    // 3. Identifier les recettes qui contiennent cet ingrédient
    Object.entries(recipes).forEach(([recipeId, recipe]) => {
      // Vérifier si la recette contient l'ingrédient
      // Selon votre structure, ça peut être recipe.ingredients, recipe.ingredientsList, etc.
      const ingredients = recipe.ingredients || recipe.ingredientsList || [];
      
      const containsIngredient = ingredients.some(ing => {
        // L'ingrédient peut être stocké de différentes façons :
        // - Comme objet : { id: "123", ... }
        // - Comme string : "123"
        // - Comme barcode dans un objet : { barcode: "123", ... }
        if (typeof ing === 'string') {
          return ing === ingredientId;
        }
        return ing.id === ingredientId || ing.barcode === ingredientId;
      });

      if (containsIngredient) {
        recipesToDelete.push(recipeId);
      }
    });

    // 4. Supprimer les recettes identifiées
    for (const recipeId of recipesToDelete) {
      try {
        await deleteRecipeFromUserCollection(userId, recipeId);
        deletedRecipesCount++;
      } catch (error) {
        console.error(`❌ Erreur lors de la suppression de la recette ${recipeId}:`, error);
      }
    }

    // 5. Supprimer l'ingrédient
    delete data[ingredientId];
    await setDoc(docRef, data);
    return { deletedRecipes: deletedRecipesCount };
  } catch (error) {
    console.error("❌ Erreur lors de la suppression de l'ingrédient :", error);
    throw error;
  }
}

/**
 * Met à jour un ingrédient en le passant en favori (favoris = true).
 * @param {string} userId - L'ID de l'utilisateur.
 * @param {string} barcode - Le code barre (clé de l'ingrédient).
 */
export async function setIngredientAsFavorite(userId, barcode, isFavorite = true) {
  const docRef = doc(db, "Ingredients", `${userId}-ingredients`);

  try {
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;

    const data = docSnap.data();
    if (!Object.prototype.hasOwnProperty.call(data, barcode)) return;

    const updatedIngredient = {
      ...data[barcode],
      favorite: isFavorite, // ← mise à jour ici
    };

    await setDoc(docRef, {
      ...data,
      [barcode]: updatedIngredient,
    });

  } catch (error) {
    console.error("Erreur favori :", error);
    throw error;
  }
}

/**
 * Récupère les ingrédients d'un utilisateur.
 * @param {string} userId - L'ID de l'utilisateur.
 * @returns {Promise<Object>} Une map d'ingrédients ou un objet vide.
 */
export async function getUserIngredients(userId) {
  const docRef = doc(db, "Ingredients", `${userId}-ingredients`);

  try {
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.log("Aucun ingrédient trouvé.");
      return {};
    }

    const data = docSnap.data();
    return data;
  } catch (error) {
    console.error("Erreur lors de la récupération des ingrédients :", error);
    throw error;
  }
}

/**
 * Met à jour (ou crée) une ou plusieurs entrées dans le document Ingredients/<user>-ingredients
 * entriesMap: { [id]: { ...fields } }
 */
export async function updateProduct(userId, entriesMap = {}) {
  if (!userId || !entriesMap || !Object.keys(entriesMap).length) return { updated: 0 };

  const docRef = doc(db, "Ingredients", `${userId}-ingredients`);

  try {
    await setDoc(docRef, entriesMap, { merge: true });
    return { updated: Object.keys(entriesMap).length };
  } catch (err) {
    console.error('❌ Erreur updateProduct:', err);
    throw err;
  }
}

export async function searchFoodByName(query) {
  try {
    const params = new URLSearchParams({
      search_terms: query,
      search_simple: "1",
      action: "process",
      json: "1",
      page_size: "10",
      fields: [
        "code", "product_name", "product_name_fr", "brands",
        "image_front_url", "nutriscore_grade", "nutriscore_data",
        "nova_group", "nutriments", "serving_quantity", "serving_size",
        "allergens_tags", "additives_tags", "labels",
      ].join(","),
    });
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v2/search?${params}`
    );
    if (!res.ok) throw new Error(`OFA HTTP ${res.status}`);
    const data = await res.json();
    return data.products || [];
  } catch (e) {
    console.error("❌ Erreur API OpenFoodFacts:", e);
    return [];
  }
}

// Groupe 1 - Aliments non transformés ou transformés minimalement
// Groupe 2 - Ingrédients culinaires transformés
// Groupe 3 - Aliments transformés
// Groupe 4 - Produits alimentaires et boissons ultra-transformés
export function getNovaStringAndColor(novaGroup) {
  let color = 'grey';

  switch (novaGroup) {
    case 1:
      color = 'green'; break;
    case 2:
      color = 'yellow'; break;
    case 3:
      color = 'orange'; break;
    case 4:
      color = 'red'; break;
  }

  return {
    string: `nova.${novaGroup}`, // clef à passer à $t()
    color
  };
}

/**
 * Récupère les détails des ingrédients d'un utilisateur à partir d'une liste de codes-barres.
 * @param {string} userId - ID de l'utilisateur
 * @param {string[]} barcodes - Liste des codes-barres à rechercher
 * @returns {Promise<Array>} - Liste d'ingrédients complets [{id, productName, productBrand, nutritionPer100g, ...}]
 */
/**
 * Récupère les détails des ingrédients à partir d'une liste d'IDs.
 * Ordre de priorité :
 *   1. BaseIngredients/all       (ingrédients de base visibles)
 *   2. HiddenBaseIngredients/all (ingrédients de base cachés)
 *   3. Ingredients/{userId}-ingredients (collection perso — fallback)
 *
 * Cela garantit que les recettes de base affichent toujours la version
 * admin (avec image, macros, etc.) et non la copie perso de l'admin.
 */
export async function getIngredientsByBarcodes(userId, barcodes = []) {
  if (!userId || !Array.isArray(barcodes) || !barcodes.length) {
    return []
  }

  try {
    // 1. Charger les trois sources en parallèle
    const [baseData, hiddenData, userData] = await Promise.all([
      getBaseIngredients(),
      getHiddenBaseIngredients(),
      getUserIngredients(userId),
    ])

    // 2. Fusionner : user en dernier = plus basse priorité
    // Si un ID existe dans base ET user, c'est la version base qui gagne
    const merged = {
      ...userData,    // priorité 3 (la plus basse)
      ...hiddenData,  // priorité 2
      ...baseData,    // priorité 1 (la plus haute)
    }

    // 3. Filtrer sur les IDs demandés
    const results = []
    for (const code of barcodes) {
      if (merged[code]) {
        results.push({ id: code, ...merged[code] })
      }
    }

    return results
  } catch (error) {
    console.error("❌ Erreur getIngredientsByBarcodes:", error)
    return []
  }
}